import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { withAuthorization } from '../Session';
import { withFirebase } from '../Firebase';
import Moment from 'moment';
import 'moment-timezone';
import NumberFormat from 'react-number-format';
import Button from 'react-bootstrap/Button';
import * as ROUTES from '../../constants/routes';

const CampaignDetailsPage = ({ firebase }) => {
    const { uid, campaignId } = useParams();
    const [campaign, setCampaign] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Subcollection data
    const [likes, setLikes] = useState([]);
    const [dislikes, setDislikes] = useState([]);
    const [favorites, setFavorites] = useState([]);
    const [seen, setSeen] = useState([]);
    const [usersThatUsed, setUsersThatUsed] = useState([]);
    const [clicks, setClicks] = useState([]);

    // Loading states for subcollections
    const [loadingLikes, setLoadingLikes] = useState(false);
    const [loadingDislikes, setLoadingDislikes] = useState(false);
    const [loadingFavorites, setLoadingFavorites] = useState(false);
    const [loadingSeen, setLoadingSeen] = useState(false);
    const [loadingUsed, setLoadingUsed] = useState(false);
    const [loadingClicks, setLoadingClicks] = useState(false);

    // Active tab for subcollections
    const [activeTab, setActiveTab] = useState('used');

    useEffect(() => {
        if (!uid || !campaignId) {
            setError('Missing uid or campaignId');
            setLoading(false);
            return;
        }

        // Fetch campaign data
        firebase.db.collection('company').doc(uid)
            .collection('campaign').doc(campaignId)
            .get()
            .then(doc => {
                if (doc.exists) {
                    setCampaign({ id: doc.id, ...doc.data() });
                } else {
                    setError('Campaign not found');
                }
                setLoading(false);
            })
            .catch(err => {
                setError(err.message);
                setLoading(false);
            });
    }, [firebase, uid, campaignId]);

    const fetchSubcollection = (collectionName, setData, setLoadingState) => {
        setLoadingState(true);

        let query = firebase.db.collection('company').doc(uid)
            .collection('campaign').doc(campaignId)
            .collection(collectionName);

        // Only order by time if not usersThatUsed (which may not have time field)
        if (collectionName !== 'usersThatUsed') {
            query = query.orderBy('time', 'desc');
        }

        query.get()
            .then(snapshot => {
                const data = [];
                snapshot.forEach(doc => {
                    data.push({ id: doc.id, ...doc.data() });
                });
                setData(data);
                setLoadingState(false);
            })
            .catch(err => {
                console.error(`Error fetching ${collectionName}:`, err);
                setLoadingState(false);
            });
    };

    const loadTabData = (tab) => {
        setActiveTab(tab);
        switch (tab) {
            case 'likes':
                if (likes.length === 0) fetchSubcollection('likes', setLikes, setLoadingLikes);
                break;
            case 'dislikes':
                if (dislikes.length === 0) fetchSubcollection('dislikes', setDislikes, setLoadingDislikes);
                break;
            case 'favorites':
                if (favorites.length === 0) fetchSubcollection('favorites', setFavorites, setLoadingFavorites);
                break;
            case 'seen':
                if (seen.length === 0) fetchSubcollection('seen', setSeen, setLoadingSeen);
                break;
            case 'used':
                if (usersThatUsed.length === 0) fetchSubcollection('usersThatUsed', setUsersThatUsed, setLoadingUsed);
                break;
            case 'clicks':
                if (clicks.length === 0) fetchSubcollection('click', setClicks, setLoadingClicks);
                break;
            default:
                break;
        }
    };

    useEffect(() => {
        if (campaign) {
            loadTabData('used');
        }
    }, [campaign]);

    const formatTimestamp = (timestamp) => {
        if (!timestamp) return '-';
        const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
        return Moment.tz(date, 'Europe/Belgrade').format('DD.MM.YYYY HH:mm');
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 p-8">
                <div className="max-w-6xl mx-auto">
                    <div className="animate-pulse">
                        <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
                        <div className="h-64 bg-gray-200 rounded mb-4"></div>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-gray-50 p-8">
                <div className="max-w-6xl mx-auto">
                    <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                        <h2 className="text-red-800 font-semibold mb-2">Error</h2>
                        <p className="text-red-600">{error}</p>
                        <Link to={`${ROUTES.DASHBOARD}/${uid}`} className="text-blue-600 hover:underline mt-4 inline-block">
                            Back to Dashboard
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    const TabButton = ({ tab, label, count }) => (
        <button
            onClick={() => loadTabData(tab)}
            className={`px-4 py-2 font-medium text-sm rounded-t-lg border-b-2 transition-colors ${
                activeTab === tab
                    ? 'border-blue-500 text-blue-600 bg-white'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
        >
            {label} {count !== undefined && <span className="text-gray-400">({count})</span>}
        </button>
    );

    const ClicksTable = ({ data, loading, formatTimestamp }) => {
        if (loading) {
            return <div className="p-4 text-center text-gray-500">Loading...</div>;
        }
        if (data.length === 0) {
            return <div className="p-4 text-center text-gray-500">No clicks recorded</div>;
        }

        // Group clicks by type for summary
        const clicksByType = data.reduce((acc, click) => {
            const type = click.type || 'unknown';
            acc[type] = (acc[type] || 0) + 1;
            return acc;
        }, {});

        const typeLabels = {
            'image': 'Image',
            'carousel_button': 'Carousel Button',
            'carousel_image': 'Carousel Image',
            'link_from_offer': 'Link from Offer',
            'unknown': 'Unknown'
        };

        const typeColors = {
            'image': 'bg-blue-100 text-blue-800',
            'carousel_button': 'bg-green-100 text-green-800',
            'carousel_image': 'bg-purple-100 text-purple-800',
            'link_from_offer': 'bg-orange-100 text-orange-800',
            'unknown': 'bg-gray-100 text-gray-800'
        };

        return (
            <div>
                {/* Summary by type */}
                <div className="flex flex-wrap gap-3 mb-4">
                    {Object.entries(clicksByType).map(([type, count]) => (
                        <div key={type} className={`px-3 py-1 rounded-full text-sm font-medium ${typeColors[type] || typeColors['unknown']}`}>
                            {typeLabels[type] || type}: {count}
                        </div>
                    ))}
                    <div className="px-3 py-1 rounded-full text-sm font-medium bg-gray-800 text-white">
                        Total: {data.length}
                    </div>
                </div>

                {/* Clicks table */}
                <div className="overflow-x-auto max-h-96">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50 sticky top-0">
                            <tr>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">#</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">User UID</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">URL</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Time</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {data.map((click, index) => (
                                <tr key={click.id} className="hover:bg-gray-50">
                                    <td className="px-4 py-2 text-sm text-gray-500">{index + 1}</td>
                                    <td className="px-4 py-2">
                                        <span className={`px-2 py-1 rounded text-xs font-medium ${typeColors[click.type] || typeColors['unknown']}`}>
                                            {typeLabels[click.type] || click.type || 'Unknown'}
                                        </span>
                                    </td>
                                    <td className="px-4 py-2 text-sm font-mono text-gray-900">{click.uid || '-'}</td>
                                    <td className="px-4 py-2 text-sm text-blue-600 max-w-xs truncate">
                                        {click.url ? (
                                            <a href={click.url} target="_blank" rel="noopener noreferrer" className="hover:underline" title={click.url}>
                                                {click.url.length > 50 ? click.url.substring(0, 50) + '...' : click.url}
                                            </a>
                                        ) : '-'}
                                    </td>
                                    <td className="px-4 py-2 text-sm text-gray-500">{formatTimestamp(click.time)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    };

    const UserTable = ({ data, loading, showValue = false }) => {
        if (loading) {
            return <div className="p-4 text-center text-gray-500">Loading...</div>;
        }
        if (data.length === 0) {
            return <div className="p-4 text-center text-gray-500">No data</div>;
        }
        return (
            <div className="overflow-x-auto max-h-96">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50 sticky top-0">
                        <tr>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">#</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">UID</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Time</th>
                            {showValue && (
                                <>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Store</th>
                                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Value</th>
                                </>
                            )}
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {data.map((item, index) => (
                            <tr key={item.id} className="hover:bg-gray-50">
                                <td className="px-4 py-2 text-sm text-gray-500">{index + 1}</td>
                                <td className="px-4 py-2 text-sm font-mono text-gray-900">{item.uid || item.id}</td>
                                <td className="px-4 py-2 text-sm text-gray-500">{formatTimestamp(item.time)}</td>
                                {showValue && (
                                    <>
                                        <td className="px-4 py-2 text-sm text-gray-500">{item.storeId || '-'}</td>
                                        <td className="px-4 py-2 text-sm text-right text-gray-900">
                                            <NumberFormat value={item.value || 0} displayType="text" thousandSeparator="." decimalSeparator="," />
                                        </td>
                                    </>
                                )}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    };

    return (
        <div className="min-h-screen bg-gray-50 p-8">
            <div className="max-w-6xl mx-auto">
                {/* Back link */}
                <Link to={`${ROUTES.DASHBOARD}/${uid}`} className="text-blue-600 hover:underline mb-4 inline-flex items-center gap-1">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                    Back to Dashboard
                </Link>

                {/* Campaign Header */}
                <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
                    <div className="flex gap-6">
                        {campaign.image && (
                            <img
                                src={campaign.image}
                                alt="Campaign"
                                className="w-48 h-48 object-cover rounded-lg"
                            />
                        )}
                        <div className="flex-1">
                            <div className="flex items-start justify-between">
                                <div>
                                    <h1 className="text-2xl font-bold text-gray-900 mb-2">
                                        {campaign.title || campaign.offerValue?.substring(0, 50) || campaignId}
                                    </h1>
                                    <span className="inline-block px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
                                        {campaign.campaignType || 'N/A'}
                                    </span>
                                </div>
                                <div className="text-right text-sm text-gray-500">
                                    <div>ID: <span className="font-mono">{campaignId}</span></div>
                                    <div>{formatTimestamp(campaign.time)}</div>
                                </div>
                            </div>

                            {campaign.offerValue && (
                                <p className="mt-4 text-gray-600">{campaign.offerValue}</p>
                            )}

                            {campaign.body && (
                                <p className="mt-2 text-gray-600">{campaign.body}</p>
                            )}
                        </div>
                    </div>
                </div>

                {/* Carousel Items */}
                {campaign.carouselItems && campaign.carouselItems.length > 0 && (
                    <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
                        <h2 className="text-lg font-semibold text-gray-900 mb-4">
                            Carousel Items ({campaign.carouselItems.length})
                        </h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {campaign.carouselItems.map((item, index) => (
                                <div key={index} className="border rounded-lg overflow-hidden hover:shadow-md transition-shadow">
                                    {item.imageUrl && (
                                        <a href={item.buttonLink || '#'} target="_blank" rel="noopener noreferrer">
                                            <img
                                                src={item.imageUrl}
                                                alt={item.buttonText || `Carousel ${index + 1}`}
                                                className="w-full h-48 object-cover hover:opacity-90 transition-opacity"
                                            />
                                        </a>
                                    )}
                                    <div className="p-3">
                                        <div className="text-sm font-medium text-gray-900 mb-2">
                                            {item.buttonText || `Slide ${index + 1}`}
                                        </div>
                                        {item.buttonLink && (
                                            <a
                                                href={item.buttonLink}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-xs text-blue-600 hover:underline break-all"
                                            >
                                                {item.buttonLink.length > 60 ? item.buttonLink.substring(0, 60) + '...' : item.buttonLink}
                                            </a>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Stats Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 mb-6">
                    <div className="bg-white p-4 rounded-lg shadow-sm border">
                        <div className="text-sm text-gray-500">Target Users</div>
                        <div className="text-xl font-bold text-gray-900">
                            <NumberFormat value={campaign.numberOfUserToTarget || campaign.userTotal || 0} displayType="text" thousandSeparator="." decimalSeparator="," />
                        </div>
                    </div>
                    <div className="bg-white p-4 rounded-lg shadow-sm border">
                        <div className="text-sm text-gray-500">Android Tokens</div>
                        <div className="text-xl font-bold text-green-600">
                            <NumberFormat value={campaign.tokenCountAndroid || 0} displayType="text" thousandSeparator="." decimalSeparator="," />
                        </div>
                    </div>
                    <div className="bg-white p-4 rounded-lg shadow-sm border">
                        <div className="text-sm text-gray-500">iOS Tokens</div>
                        <div className="text-xl font-bold text-blue-600">
                            <NumberFormat value={campaign.tokenCountIOS || 0} displayType="text" thousandSeparator="." decimalSeparator="," />
                        </div>
                    </div>
                    <div className="bg-white p-4 rounded-lg shadow-sm border cursor-pointer hover:border-orange-300 transition-colors" onClick={() => loadTabData('clicks')}>
                        <div className="text-sm text-gray-500">Clicks</div>
                        <div className="text-xl font-bold text-orange-600">
                            {clicks.length > 0 ? (
                                <NumberFormat value={clicks.length} displayType="text" thousandSeparator="." decimalSeparator="," />
                            ) : (
                                <span className="text-gray-400">-</span>
                            )}
                        </div>
                    </div>
                    <div className="bg-white p-4 rounded-lg shadow-sm border">
                        <div className="text-sm text-gray-500">Campaign Cost</div>
                        <div className="text-xl font-bold text-purple-600">
                            <NumberFormat value={campaign.campaignCost || 0} displayType="text" thousandSeparator="." decimalSeparator="," />
                        </div>
                    </div>
                    <div className="bg-white p-4 rounded-lg shadow-sm border">
                        <div className="text-sm text-gray-500">Used Only Once</div>
                        <div className="text-xl font-bold text-gray-900">
                            {campaign.usedOnlyOnce ? 'Yes' : 'No'}
                        </div>
                    </div>
                    <div className="bg-white p-4 rounded-lg shadow-sm border">
                        <div className="text-sm text-gray-500">City</div>
                        <div className="text-xl font-bold text-gray-900">
                            {campaign.city || 'All'}
                        </div>
                    </div>
                </div>

                {/* Subcollection Tabs */}
                <div className="bg-white rounded-lg shadow-sm border">
                    <div className="border-b px-4 pt-4 flex gap-2 flex-wrap">
                        <TabButton tab="used" label="Users That Used" count={usersThatUsed.length || undefined} />
                        <TabButton tab="clicks" label="Clicks" count={clicks.length || undefined} />
                        <TabButton tab="seen" label="Seen" count={seen.length || undefined} />
                        <TabButton tab="likes" label="Likes" count={likes.length || undefined} />
                        <TabButton tab="dislikes" label="Dislikes" count={dislikes.length || undefined} />
                        <TabButton tab="favorites" label="Favorites" count={favorites.length || undefined} />
                    </div>

                    <div className="p-4">
                        {activeTab === 'used' && <UserTable data={usersThatUsed} loading={loadingUsed} showValue={true} />}
                        {activeTab === 'clicks' && <ClicksTable data={clicks} loading={loadingClicks} formatTimestamp={formatTimestamp} />}
                        {activeTab === 'seen' && <UserTable data={seen} loading={loadingSeen} />}
                        {activeTab === 'likes' && <UserTable data={likes} loading={loadingLikes} />}
                        {activeTab === 'dislikes' && <UserTable data={dislikes} loading={loadingDislikes} />}
                        {activeTab === 'favorites' && <UserTable data={favorites} loading={loadingFavorites} />}
                    </div>
                </div>
            </div>
        </div>
    );
};

const condition = authUser => !!authUser;

export default withFirebase(withAuthorization(condition)(CampaignDetailsPage));
